import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  X,
  QrCode,
  Sparkles,
  Loader2,
  Save,
  HelpCircle,
} from "lucide-react";
import {
  getStudioUpiVpaAPI,
  saveStudioUpiVpaAPI,
} from "../../../../Redux/ApiCalls/Dashboard/CustomerTransationApi";
import { showSuccessToast } from "../../Utlis/Toast";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  FormCard,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  ModalActions,
  SecondaryButton,
  PrimaryButton,
} from "../../../organisms/Dashboard/quotationMasterListShared";
import {
  Field,
  Label,
  Input,
  ErrorSpan,
  FormLabelIcon,
  FormGradientDefs,
} from "../../../molecules/Dashboard/Member/memberFormStyles";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

const ReqStar = styled.span`
  color: #e44862;
  margin-left: 2px;
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(49, 166, 106, 0.08);
  border: 1px solid rgba(49, 166, 106, 0.18);
  font-family: Montserrat, sans-serif;
  font-size: 12.5px;
  line-height: 1.45;
  color: rgba(23, 23, 23, 0.75);

  strong {
    color: #171717;
  }
`;

interface ManageUpiModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialVpa?: string | null;
  initialPayeeName?: string | null;
}

export default function ManageUpiModal({
  isOpen,
  onClose,
  onSuccess,
  initialVpa = "",
  initialPayeeName = "",
}: ManageUpiModalProps) {
  const [upiVpa, setUpiVpa] = useState(initialVpa || "");
  const [payeeName, setPayeeName] = useState(initialPayeeName || "");
  const [initialFetchedVpa, setInitialFetchedVpa] = useState(initialVpa || "");
  const [initialFetchedPayee, setInitialFetchedPayee] = useState(initialPayeeName || "");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchCurrentDetails();
    }
  }, [isOpen]);

  const fetchCurrentDetails = async () => {
    setFetching(true);
    try {
      const res = await getStudioUpiVpaAPI();
      if (res && res.data) {
        const vpa = res.data.upi_vpa || "";
        const payee = res.data.upi_payee_name || "";
        setUpiVpa(vpa);
        setPayeeName(payee);
        setInitialFetchedVpa(vpa);
        setInitialFetchedPayee(payee);
      }
    } catch {
      // ignore
    } finally {
      setFetching(false);
    }
  };

  const isChanged =
    upiVpa.trim().toLowerCase() !== initialFetchedVpa.trim().toLowerCase() &&
    upiVpa.trim().length > 0;

  const validate = () => {
    const trimmed = upiVpa.trim();
    if (!trimmed) return "UPI ID is required (e.g. name@bank)";
    if (!trimmed.includes("@") || trimmed.startsWith("@") || trimmed.endsWith("@")) {
      return "Invalid UPI ID. Format should be name@bank (e.g. studio@okhdfcbank)";
    }
    return "";
  };

  const handleSave = async () => {
    const vError = validate();
    if (vError) {
      setError(vError);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await saveStudioUpiVpaAPI({
        upi_vpa: upiVpa.trim().toLowerCase(),
        upi_payee_name: payeeName.trim() || undefined,
      });

      if (res && res.status) {
        showSuccessToast("Studio UPI ID saved successfully!");
        onSuccess?.();
        onClose();
      } else if (res?.error) {
        setError(res.error);
      }
    } catch {
      setError("Failed to save UPI ID. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <FormGradientDefs />
      <ModalOverlay onClick={onClose}>
        <ModalShell style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
          <ModalHero>
            <HeroGlow />
            <HeroRow>
              <HeroText>
                <HeroBadge>
                  <Sparkles size={11} />
                  Direct Payments
                </HeroBadge>
                <ModalTitle>Studio UPI ID</ModalTitle>
                <ModalSubtitle>
                  Configure your UPI ID to collect 0% fee payments directly into your bank account.
                </ModalSubtitle>
              </HeroText>
              <CloseBtn type="button" onClick={onClose} aria-label="Close">
                <X size={18} />
              </CloseBtn>
            </HeroRow>
          </ModalHero>

          <ModalBody>
            {error && <ErrorSpan style={{ display: "block" }}>{error}</ErrorSpan>}

            <FormCard>
              <InfoBox>
                <HelpCircle size={18} color="#31a66a" style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                  Payments via Direct UPI QR go <strong>directly to your bank account </strong>.
                  Make sure your UPI ID is active and can receive payments.
                </div>
              </InfoBox>

              <Field>
                <Label htmlFor="upiVpa">
                  <FormLabelIcon icon={QrCode} />
                  UPI ID (VPA) <ReqStar>*</ReqStar>
                </Label>
                <Input
                  id="upiVpa"
                  placeholder="e.g. yourstudio@okhdfcbank or 9876543210@paytm"
                  value={upiVpa}
                  onChange={(e) => setUpiVpa(e.target.value.toLowerCase())}
                  disabled={loading || fetching}
                />
              </Field>
            </FormCard>
          </ModalBody>

          <ModalActions style={{ justifyContent: "flex-end" }}>
            <div style={{ display: "flex", gap: 10 }}>
              <SecondaryButton type="button" onClick={onClose} disabled={loading}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={handleSave}
                disabled={loading || fetching || !isChanged}
                style={{
                  opacity: isChanged ? 1 : 0.5,
                  cursor: isChanged ? "pointer" : "not-allowed",
                }}
              >
                {loading ? <Spinner size={16} /> : <Save size={16} />}
                {loading ? "Saving..." : "Save UPI ID"}
              </PrimaryButton>
            </div>
          </ModalActions>
        </ModalShell>
      </ModalOverlay>
    </>,
    document.body
  );
}
